/** Old URL → new URL, in one place (plan 1-2).
 *
 *  adfusion moves no address: every old page keeps its URL and its
 *  replacement is built at the same one, so there are no rules. Above all
 *  `/settings/google` stays — it is the OAuth redirect URI registered in Google
 *  Console (env GOOGLE_REDIRECT_URI) and comes back with ?code=&scope=&state=.
 *  Nothing else on the server links into the console (no Telegram or notice
 *  links in internal/ or cmd/ — checked 10-21).
 *
 *  The machinery stays so a later move has one place to go: a rule turns on
 *  in the Phase that builds its target page, App.tsx stops mounting the old
 *  page, and the rule keeps the query. */

import { Navigate, Route, matchPath, useLocation, useParams } from 'react-router-dom'

type Params = Record<string, string | undefined>

export interface RedirectRule {
  /** A path or a route pattern (`/apps/:id`), exactly as App.tsx mounts the
   *  old page. */
  from: string
  /** The Phase that builds the target page and turns this rule on. */
  phase: number
  active: boolean
  to: (query: URLSearchParams, params: Params) => string
}

export function withQuery(path: string, q: URLSearchParams): string {
  const s = q.toString()
  return s ? `${path}?${s}` : path
}

export const REDIRECTS: RedirectRule[] = []

/** Where an old URL goes. Ignores `active`, so tests can pin a rule before
 *  its Phase turns it on. Null when the path has no rule. */
export function resolveRedirect(pathname: string, search = ''): string | null {
  for (const rule of REDIRECTS) {
    const m = matchPath({ path: rule.from, end: true }, pathname)
    if (m) return rule.to(new URLSearchParams(search.startsWith('?') ? search.slice(1) : search), m.params)
  }
  return null
}

/** Paths whose old page has been replaced — App.tsx stops mounting them. */
export function isRedirected(path: string): boolean {
  return REDIRECTS.some((r) => r.active && r.from === path)
}

function RedirectTo({ rule }: { rule: RedirectRule }) {
  const { search } = useLocation()
  const params = useParams()
  return <Navigate replace to={rule.to(new URLSearchParams(search), params)} />
}

/** The live rules as routes, for App.tsx. */
export function redirectRoutes() {
  return REDIRECTS.filter((r) => r.active).map((r) => (
    <Route key={r.from} path={r.from} element={<RedirectTo rule={r} />} />
  ))
}
